/**
 * User profile helpers — Phase 5a replacement.
 * Delegates to /api/users/* endpoints on dash-api instead of Firestore.
 */

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) throw new Error(`${opts.method || 'GET'} ${path} → ${resp.status}`);
    return resp.json();
}

/** dash-api UserPublicView uses `userId`; legacy Firestore-shaped code expects `id`/`uid`. */
export function normalizeApiUser(raw) {
    if (!raw) return null;
    const id = raw.id ?? raw.userId ?? raw.uid ?? null;
    if (!id) return raw;
    const profilePicture =
        raw.profilePicture
        || raw.profile_picture_url
        || raw.photoURL
        || (raw.hasAvatar ? `/api/users/${encodeURIComponent(id)}/avatar` : undefined);
    return {
        ...raw,
        id,
        userId: id,
        uid: id,
        // Ticket 01: the real user's `username` is the "handle" used throughout (Forecast personas,
        // Experts page, author-chip/citation lookups) — legacy Expert-shaped code reads `.handle`.
        handle: raw.username || id,
        arr: raw.areasOfResponsibility,
        expertise: raw.bio,
        specialties: raw.expertiseKeywords,
        ...(profilePicture ? {profilePicture} : {}),
    };
}

function normalizeUsers(list) {
    return (list || []).map(normalizeApiUser).filter(Boolean);
}

export const getUserDoc = async (userId) => {
    if (!userId) return null;
    const data = await apiFetch(`/api/users/${encodeURIComponent(userId)}`);
    return normalizeApiUser(data);
};

export const getUserRole = async (userId) => {
    const user = await getUserDoc(userId);
    return user?.role || null;
};

export const searchUsersByName = async (input, opts = {}) => {
    if (!input || typeof input !== 'string' || !input.trim()) return [];
    const limit = typeof opts.limit === 'number' ? opts.limit : 20;
    const data = await apiFetch(
        `/api/users/search?q=${encodeURIComponent(input.trim())}&limit=${limit}`
    );
    return normalizeUsers(Array.isArray(data) ? data : []);
};

export const getUsersByIds = async (ids = []) => {
    const unique = Array.from(new Set((ids || []).filter(Boolean)));
    if (unique.length === 0) return [];
    const data = await apiFetch(
        `/api/users?ids=${unique.map(encodeURIComponent).join(',')}`
    );
    return normalizeUsers(Array.isArray(data) ? data : []);
};

export const getUsersByRole = async (role) => {
    const data = await apiFetch(
        `/api/users/search?role=${encodeURIComponent(role)}&limit=100`
    );
    return normalizeUsers(Array.isArray(data) ? data : []);
};

/**
 * Ticket 01: real users flagged as analysts (existing `is_analyst`/`analyst`-role signal — no new
 * tag is added to users). Used by Forecast's persona picker and Analyst Perspectives' expert picker.
 */
export const getAnalystUsers = async () => {
    const data = await apiFetch('/api/users/analysts');
    return normalizeUsers(Array.isArray(data) ? data : []);
};

/**
 * The Experts page listing: real users flagged with the 'expert' role, or the existing analyst
 * signal ('is_analyst'/'analyst' role). Replaces the prior unfiltered "list every real user"
 * behavior (subscribeToAllUsers).
 */
export const getExpertUsers = async () => {
    const data = await apiFetch('/api/users/experts');
    return normalizeUsers(Array.isArray(data) ? data : []);
};

/** Subscribe (fetch-once) to expert-or-analyst users for the Experts page list. */
export const subscribeToExpertUsers = (onChange) => {
    let cancelled = false;
    getExpertUsers()
        .then((data) => { if (!cancelled) onChange?.(data); })
        .catch((err) => { console.error('[users] subscribeToExpertUsers', err); if (!cancelled) onChange?.([]); });
    return () => { cancelled = true; };
};

/**
 * Ticket 01: resolve a real user by handle (username), falling back to a raw userId. Used by the
 * Experts page detail route and author-chip/citation lookups.
 */
export const getUserByHandle = async (handle) => {
    if (!handle) return null;
    const data = await apiFetch(`/api/users/by-handle/${encodeURIComponent(handle)}`);
    return normalizeApiUser(data);
};

/** Ticket 01: subscribe (fetch-once) to a real user's profile by handle for the Experts page detail route. */
export const subscribeToUserByHandle = (handle, onChange) => {
    if (!handle) {
        onChange?.(null);
        return () => {};
    }
    let cancelled = false;
    getUserByHandle(handle)
        .then((data) => { if (!cancelled) onChange?.(data); })
        .catch((err) => { console.error('[users] subscribeToUserByHandle', err); if (!cancelled) onChange?.(null); });
    return () => { cancelled = true; };
};

/** List users for share picker / mentions (empty q returns all active users). */
export const getAllUsers = async (limit = 50, opts = {}) => {
    const includeMachineUsers = opts.includeMachineUsers === true;
    const data = await apiFetch(`/api/users/search?q=&limit=${limit}`);
    return normalizeUsers(Array.isArray(data) ? data : []).filter((u) => {
        if (!u?.id || u.id === 'ai') return false;
        if (!includeMachineUsers && u.role === 'machine') return false;
        return true;
    });
};

/**
 * Ticket 01: subscribe (fetch-once) to all real users. No longer the Experts page's data source
 * (see `subscribeToExpertUsers`) — used by mentions/share-dialog/chat-picker features that call
 * `getAllUsers` directly. Default limit is capped at 100 to match the backend's
 * `/api/users/search` `@Max(100)` validation on the `limit` param — a request above that cap is
 * rejected with a 400, which surfaces as a silently-empty list.
 */
export const subscribeToAllUsers = (onChange, limit = 100) => {
    let cancelled = false;
    getAllUsers(limit)
        .then((data) => { if (!cancelled) onChange?.(data); })
        .catch((err) => { console.error('[users] subscribeToAllUsers', err); if (!cancelled) onChange?.([]); });
    return () => { cancelled = true; };
};

export const updateUserProfile = async (userId, profile = {}) => {
    if (!userId) throw new Error('userId is required');
    const {firstName, lastName, ...rest} = profile || {};
    const normalized = {
        ...(firstName != null ? {firstName: String(firstName).toLowerCase()} : {}),
        ...(lastName != null ? {lastName: String(lastName).toLowerCase()} : {}),
        ...rest,
    };
    const data = await apiFetch(`/api/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: JSON.stringify(normalized),
    });
    return data || {id: userId, ...normalized};
};

/**
 * Ticket 02: write an auto-generated expertise summary (bio/expertiseKeywords) onto a real
 * user's own profile, fired after their approved FINTELs are (re)aggregated.
 */
export const updateExpertiseProfile = async (userId, {bio, expertiseKeywords, expertiseUpdatedAt}) => {
    if (!userId) throw new Error('userId is required');
    return apiFetch(`/api/users/${encodeURIComponent(userId)}/expertise-profile`, {
        method: 'PATCH',
        body: JSON.stringify({bio, expertiseKeywords, expertiseUpdatedAt}),
    });
};
